import CoreApi from '@/api/CoreApi';
import type {
  CatalogueProject,
  CatalogueProjectCreate,
  CatalogueProjectUpdate,
  CatalogueProjectBom,
  CatalogueProjectBomCreate,
  CatalogueProjectBomUpdate,
  CatalogueProjectBomItem,
  CatalogueProjectBomItemCreate,
  CatalogueProjectBomItemUpdate,
  CatalogueProjectBomDuplicateType,
} from '@/models/Projects';
import { CatalogueProjectBomType } from '@/models/Projects';

export async function getProjects(): Promise<CatalogueProject[]> {
  const response = await CoreApi.axiosClient.get('/catalogue/projects');
  return response.data;
}

export async function createProject(
  project: CatalogueProjectCreate,
): Promise<CatalogueProject> {
  const response = await CoreApi.axiosClient.post(
    '/catalogue/projects',
    project,
  );
  return response.data;
}

export async function updateProject(
  projectUuid: string,
  project: CatalogueProjectUpdate,
): Promise<CatalogueProject> {
  const response = await CoreApi.axiosClient.put(
    `/catalogue/projects/${projectUuid}`,
    project,
  );
  return response.data;
}

export async function deleteProject(projectUuid: string): Promise<void> {
  await CoreApi.axiosClient.delete(`/catalogue/projects/${projectUuid}`);
}

export async function getProjectBoms(
  projectUuid: string,
): Promise<CatalogueProjectBom[]> {
  const url = `/catalogue/boms/${projectUuid}`;

  const response = await CoreApi.axiosClient.get(url);
  return response.data;
}

export async function createProjectBom(
  payload: CatalogueProjectBomCreate,
): Promise<CatalogueProjectBom> {
  const response = await CoreApi.axiosClient.post('/catalogue/boms', payload);
  return response.data;
}

export async function updateProjectBom(
  bomUuid: string,
  payload: CatalogueProjectBomUpdate,
): Promise<CatalogueProjectBom> {
  const response = await CoreApi.axiosClient.put(
    `/catalogue/boms/${bomUuid}`,
    payload,
  );
  return response.data;
}

export async function deleteProjectBom(bomUuid: string): Promise<void> {
  await CoreApi.axiosClient.delete(`/catalogue/boms/${bomUuid}`);
}

export async function getBomItems(
  bomUuid: string,
): Promise<CatalogueProjectBomItem[]> {
  const response = await CoreApi.axiosClient.get(
    `/catalogue/boms/${bomUuid}/items`,
  );
  return response.data;
}

export async function addBomItem(
  bomUuid: string,
  item: CatalogueProjectBomItemCreate,
): Promise<CatalogueProjectBomItem> {
  const response = await CoreApi.axiosClient.post(
    `/catalogue/boms/${bomUuid}/items`,
    item,
  );
  return response.data;
}

export async function updateBomItem(
  bomUuid: string,
  itemUuid: string,
  updates: CatalogueProjectBomItemUpdate,
): Promise<CatalogueProjectBomItem> {
  const response = await CoreApi.axiosClient.put(
    `/catalogue/boms/${bomUuid}/items/${itemUuid}`,
    updates,
  );
  return response.data;
}

export async function removeBomItem(
  bomUuid: string,
  itemUuid: string,
): Promise<void> {
  await CoreApi.axiosClient.delete(
    `/catalogue/boms/${bomUuid}/items/${itemUuid}`,
  );
}

export async function duplicateProjectBom(payload: {
  targetProjectUuid: string;
  bomUuid: string;
  branchType?: CatalogueProjectBomDuplicateType;
}): Promise<CatalogueProjectBom> {
  const response = await CoreApi.axiosClient.post(
    `/catalogue/boms/duplicate`,
    payload,
  );
  return response.data;
}

export type ExportFormat = 'csv' | 'excel';

export async function exportProjectBomByType(
  bomUuid: string,
  bomType: CatalogueProjectBomType,
  format: ExportFormat = 'csv',
): Promise<Blob> {
  const endpoint =
    bomType === CatalogueProjectBomType.dbom
      ? `/catalogue/boms/export-dbom/${bomUuid}`
      : `/catalogue/boms/export-ebom/${bomUuid}`;
  const response = await CoreApi.axiosClient.get(endpoint, {
    responseType: 'blob',
    params: { format },
  });
  return response.data;
}
